// The ONE canonical fit band + the card-reason truthfulness filter (Careers consumer P0 — Phase A).
//
// Band unification: this collapses the four previously-divergent band maps (feedShared `fitBand`,
// feed `groupOf`, Today `band()`, ApplicationsPanel `fitColor`) onto a single source, derived from
// the SAME thresholds the fit engine uses in web/lib/career/fit.ts `computeFit`:
//   Strong  >= 70  (engine APPLY_NOW >=85 + HIGH >=70)
//   Good    55-69  (engine GOOD)
//   Stretch 40-54  (engine REVIEW, scored)
//   Reach   < 40   (engine LOW)
// Eligibility gates first (Not eligible); a null score is Not scored. The band is a WORD — no raw
// fit number is shown on cards (it reads as fake precision); the number + drivers live in the
// drawer's "Why this score".
//
// Pure (no React), so the UI surfaces import it and it is unit-testable (bands.test.ts).

export type BandKey = "strong" | "good" | "stretch" | "reach" | "not_eligible" | "not_scored"
export type BandTone = "success" | "info" | "warning" | "danger" | "neutral"
export interface Band { key: BandKey; label: string; tone: BandTone }

const BAND: Record<BandKey, Band> = {
  strong:       { key: "strong",       label: "Strong match", tone: "success" },
  good:         { key: "good",         label: "Good match",   tone: "info" },
  stretch:      { key: "stretch",      label: "Stretch",      tone: "warning" },
  reach:        { key: "reach",        label: "Reach",        tone: "neutral" },
  not_eligible: { key: "not_eligible", label: "Not eligible", tone: "danger" },
  not_scored:   { key: "not_scored",   label: "Not scored",   tone: "neutral" },
}

/** Eligible unless the engine marked the role ineligible. Mirrors the fit engine's `inelig` gate on
 *  eligibility.status. One definition shared by every surface (feedShared re-exports it as isEligible). */
export function isEligibleStatus(status: string | null | undefined): boolean {
  return status !== "CONFIRMED_INELIGIBLE" && status !== "LIKELY_INELIGIBLE"
}

/** The canonical band for a (score, eligible) pair. Thresholds match fit.ts `computeFit` exactly. */
export function bandFor(score: number | null | undefined, eligible: boolean): Band {
  if (!eligible) return BAND.not_eligible
  if (score == null) return BAND.not_scored
  if (score >= 70) return BAND.strong
  if (score >= 55) return BAND.good
  if (score >= 40) return BAND.stretch
  return BAND.reach
}

// Reason clauses the FEED CARD must never assert, because the data isn't reliable at feed scale:
// per-skill matched/missing (only ~2.8% of active roles list structured skills, and those are
// soft-skill prose — "communication" outranks "python"; the engine also matches against the
// structured skills list, not the résumé, so "not on your resume" would be literally false) and
// coursework (~0.9% coverage). These stay in the on-demand résumé-tailor panel, which reads the real
// résumé and is honestly grounded. Track, major, eligibility, location and the soft experience
// signal all pass. Prefix-matched against the stable clause prefixes fit.ts emits ("On-target
// track:", "Major fits:", "Skill:", "Coursework:", "Requested skill...", "Relevant work experience",
// "Location...").
const BLOCKED_CARD_REASON_PREFIXES = ["skill:", "requested skill", "coursework:"]

/** True when a strengths/gaps clause is safe to show on a feed card (release gate for reasons-first). */
export function isCardReasonTruthful(text: string): boolean {
  const t = (text || "").trim().toLowerCase()
  if (!t) return false
  return !BLOCKED_CARD_REASON_PREFIXES.some((p) => t.startsWith(p))
}
